/**
 * Custom Calculator Template
 */

import { escapeHtml, createBlockWrapper } from '../utils';
import { applyThemeOverrides, evaluateFormula } from './utils';
import type { CalculatorInput, TemplateRenderer } from './types';

export const renderCustomCalculator: TemplateRenderer = (
  config,
  _data,
  container,
  _onAction,
  themeOverrides
) => {
  const title = config.title as string | undefined;
  const inputs = (config.inputs as CalculatorInput[]) || [];
  const formula = config.formula as string;
  const resultLabel = (config.resultLabel as string) || 'Result';
  const resultPrefix = (config.resultPrefix as string) || '';
  const resultSuffix = (config.resultSuffix as string) || '';

  const wrapper = createBlockWrapper('custom-calculator');
  applyThemeOverrides(wrapper, themeOverrides);

  if (title) {
    const titleEl = document.createElement('div');
    titleEl.className = 'pms-calculator-title';
    titleEl.textContent = title;
    wrapper.appendChild(titleEl);
  }

  const form = document.createElement('div');
  form.className = 'pms-calculator-form';

  // Input values storage
  const inputValues: Record<string, number | string> = {};

  // Create inputs
  for (const input of inputs) {
    const fieldEl = document.createElement('div');
    fieldEl.className = 'pms-calculator-field';

    const label = document.createElement('label');
    label.className = 'pms-calculator-label';
    label.textContent = input.label;
    fieldEl.appendChild(label);

    let inputEl: HTMLInputElement | HTMLSelectElement;

    if (input.type === 'select' && input.options) {
      inputEl = document.createElement('select');
      inputEl.className = 'pms-calculator-input';
      for (const opt of input.options) {
        const option = document.createElement('option');
        option.value = opt;
        option.textContent = opt;
        inputEl.appendChild(option);
      }
      inputValues[input.key] = input.options[0] || '';
    } else {
      inputEl = document.createElement('input');
      inputEl.className = 'pms-calculator-input';
      inputEl.type = 'number';
      if (input.min !== undefined) inputEl.min = String(input.min);
      if (input.max !== undefined) inputEl.max = String(input.max);
      if (input.step !== undefined) inputEl.step = String(input.step);
      inputEl.value = String(input.defaultValue ?? 0);
      inputValues[input.key] = input.defaultValue ?? 0;
    }

    inputEl.dataset.key = input.key;
    inputEl.addEventListener('input', () => {
      if (inputEl instanceof HTMLInputElement) {
        inputValues[input.key] = parseFloat(inputEl.value) || 0;
      } else {
        inputValues[input.key] = inputEl.value;
      }
      updateResult();
    });

    fieldEl.appendChild(inputEl);
    form.appendChild(fieldEl);
  }

  wrapper.appendChild(form);

  // Result display
  const resultEl = document.createElement('div');
  resultEl.className = 'pms-calculator-result';
  resultEl.innerHTML = `
    <span class="pms-calculator-result-label">${escapeHtml(resultLabel)}:</span>
    <span class="pms-calculator-result-value">--</span>
  `;
  wrapper.appendChild(resultEl);

  // Calculate function
  function updateResult() {
    try {
      const result = evaluateFormula(formula, inputValues);
      const valueEl = resultEl.querySelector('.pms-calculator-result-value');
      if (valueEl) {
        const formatted = typeof result === 'number' ? result.toFixed(2) : String(result);
        valueEl.textContent = `${resultPrefix}${formatted}${resultSuffix}`;
      }
    } catch (e) {
      console.warn('[Calculator] Formula error:', e);
    }
  }

  // Initial calculation
  updateResult();

  container.appendChild(wrapper);
};
